// Partners screen — invite by email, accept pending invites, set per-partner
// privacy, view their day (read-only), disconnect.
//
// Layout (top → bottom):
//   1. Invite form — email + optional note
//   2. Pending RECEIVED invites — Accept/Decline cards
//   3. Pending SENT invites — "waiting for them to accept" cards
//   4. Connected partners — each with privacy controls + Open day / Disconnect

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Mail, Plus, Send, Users, X } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import PageTitle from "../components/PageTitle";
import {
  acceptConnection,
  disconnect,
  inviteByEmail,
  loadConnections,
  PRIVACY_DESCRIPTIONS,
  PRIVACY_LABELS,
  PRIVACY_TIERS,
  setMyPrivacy,
  type Connection,
  type PrivacyTier
} from "../lib/partners";
import {
  dismissShare,
  loadInbox,
  type SharedMealInboxItem
} from "../lib/sharing";
import { friendlyError } from "../lib/errors";

export default function Partners() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<Connection[]>([]);
  const [inbox, setInbox] = useState<SharedMealInboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // When the user has at least one connection (active OR pending sent),
  // the invite form lives behind a "+ Invite" button in the header so the
  // actual partners take the primary space. Fresh users still get it inline.
  const [inviteSheetOpen, setInviteSheetOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [conns, mealInbox] = await Promise.all([loadConnections(), loadInbox()]);
      setConnections(conns);
      setInbox(mealInbox);
      setError(null);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const received = connections.filter((c) => c.status === "pending" && !c.initiatedByMe);
  const sent = connections.filter((c) => c.status === "pending" && c.initiatedByMe);
  const active = connections.filter((c) => c.status === "accepted");
  const hasAnyConnection = active.length > 0 || sent.length > 0 || received.length > 0;

  // Successful invite from the modal → close it AND refresh, so the user
  // sees the new pending invite in the "Waiting for them" section.
  async function handleInvited() {
    await refresh();
    setInviteSheetOpen(false);
  }

  return (
    <div className="p-4 space-y-5 pb-24">
      <header className="space-y-1.5">
        <PageTitle
          right={
            hasAnyConnection ? (
              <button
                type="button"
                onClick={() => setInviteSheetOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-950 text-xs font-semibold"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                Invite
              </button>
            ) : undefined
          }
        >
          Partners
        </PageTitle>
        <p className="text-sm text-slate-400">
          {hasAnyConnection
            ? "Tap a partner to open their day. Each connection has its own privacy — you choose what they see."
            : "Track with people you eat with. Each connection has its own privacy — you choose what they see, they choose what you see."}
        </p>
      </header>

      {/* Inline invite form only when the user has no partners yet — gets
          them to first-connection ASAP. After that, invite moves behind
          the "+ Invite" header button so partners take the spotlight. */}
      {!hasAnyConnection && <InviteForm onInvited={refresh} />}

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-6 text-sm">Loading…</div>
      ) : (
        <>
          {/* Shared meals inbox — actionable, sits at the top */}
          {inbox.length > 0 && (
            <Section title="Shared with you" subtitle="Tap to log or dismiss">
              <ul className="space-y-2">
                {inbox.map((item) => (
                  <InboxCard key={item.shareId} item={item} onChange={refresh} onOpen={() => navigate(`/partners/share/${item.shareId}`)} />
                ))}
              </ul>
            </Section>
          )}

          {/* Received invites first — they need action */}
          {received.length > 0 && (
            <Section title="Invites for you" subtitle="Tap to accept">
              <ul className="space-y-2">
                {received.map((c) => (
                  <PendingReceivedCard key={c.id} connection={c} onChange={refresh} />
                ))}
              </ul>
            </Section>
          )}

          {/* Active partners */}
          {active.length > 0 && (
            <Section title="Connected" subtitle={`${active.length} ${active.length === 1 ? "partner" : "partners"}`}>
              <ul className="space-y-2">
                {active.map((c) => (
                  <ActivePartnerCard
                    key={c.id}
                    connection={c}
                    onChange={refresh}
                    onOpenDay={() => c.partnerId && navigate(`/partners/${c.partnerId}`)}
                  />
                ))}
              </ul>
            </Section>
          )}

          {/* Sent (still waiting) */}
          {sent.length > 0 && (
            <Section title="Waiting for them" subtitle="They haven't accepted yet">
              <ul className="space-y-2">
                {sent.map((c) => (
                  <PendingSentCard key={c.id} connection={c} onChange={refresh} />
                ))}
              </ul>
            </Section>
          )}

          {/* Empty state — only shows when the inline invite form below also
              has nothing to anchor against. Header CTA stays hidden because
              hasAnyConnection is false → header doesn't render the + Invite
              button. The inline InviteForm above (rendered conditionally) is
              the primary action. */}
          {!received.length && !active.length && !sent.length && !inbox.length && (
            <div className="mt-4 p-5 rounded-2xl border border-dashed border-slate-700 text-center space-y-2">
              <Users className="w-10 h-10 text-slate-600 mx-auto" strokeWidth={1.5} />
              <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                Connections show up here once someone accepts. Privacy starts on "vitals only" — you control what they see.
              </p>
            </div>
          )}
        </>
      )}

      {inviteSheetOpen && (
        <InviteSheet onInvited={handleInvited} onClose={() => setInviteSheetOpen(false)} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// InviteSheet — bottom-sheet wrapper around InviteForm for use after the
// user has at least one partner. Keeps Partners screen focused on existing
// connections while still making "invite another" a one-tap action.
// ----------------------------------------------------------------------------
function InviteSheet({
  onInvited,
  onClose
}: {
  onInvited: () => Promise<void>;
  onClose: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={onClose} />
      <div
        className="fixed inset-x-0 bottom-0 z-50 bg-slate-900 border-t border-slate-800 rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="max-w-md md:max-w-2xl mx-auto p-4 space-y-3">
          <div className="flex items-center justify-between relative">
            <div className="h-1 w-10 rounded-full bg-slate-700 mx-auto" />
            <button
              onClick={onClose}
              className="absolute right-0 w-8 h-8 rounded-full text-slate-400 hover:bg-slate-800 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <InviteForm onInvited={onInvited} />
        </div>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Invite form
// ----------------------------------------------------------------------------

function InviteForm({ onInvited }: { onInvited: () => Promise<void> }) {
  const ref = useRef<HTMLInputElement | null>(null);
  // Expose a global way to focus the input from the empty-state CTA without
  // needing context or refs threaded through the tree.
  useEffect(() => {
    (window as unknown as { __focusInvite?: () => void }).__focusInvite = () => ref.current?.focus();
    return () => {
      delete (window as unknown as { __focusInvite?: () => void }).__focusInvite;
    };
  }, []);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const result = await inviteByEmail(email.trim(), note.trim() || undefined);
      if (result.already) {
        setSuccessMsg("Already invited — check your existing connections below.");
      } else if (result.partnerExisted) {
        setSuccessMsg(`Invite sent. ${email.trim()} will see it in their Partners tab.`);
      } else {
        setSuccessMsg(
          `Invite sent. ${email.trim()} doesn't have an account yet — they'll get a sign-up link with the invite code baked in.`
        );
      }
      setEmail("");
      setNote("");
      await onInvited();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-200">
        <Mail className="w-4 h-4 text-emerald-400" />
        Invite a partner
      </div>
      <input
        ref={ref}
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="partner@email.com"
        autoComplete="email"
        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
      />
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note: 'hey honey, let's track together'"
        rows={2}
        maxLength={280}
        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 text-sm resize-none"
      />
      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={busy || !email}
          className="px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold text-sm disabled:opacity-50 flex items-center gap-2"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? "Sending…" : "Send invite"}
        </button>
        <span className="text-[11px] text-slate-500 flex-1">
          You start on "Vitals only" — you can open up later.
        </span>
      </div>
      {error && (
        <div className="p-2.5 rounded-lg bg-red-950 border border-red-800 text-red-200 text-xs">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="p-2.5 rounded-lg bg-emerald-950 border border-emerald-800 text-emerald-200 text-xs">
          {successMsg}
        </div>
      )}
    </form>
  );
}

// ----------------------------------------------------------------------------
// Cards
// ----------------------------------------------------------------------------

function InboxCard({
  item,
  onChange,
  onOpen
}: {
  item: SharedMealInboxItem;
  onChange: () => Promise<void>;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const meal = item.meal;
  const name =
    meal.estimate.items.length === 0
      ? "Untitled meal"
      : meal.estimate.items.length === 1
      ? meal.estimate.items[0].name
      : `${meal.estimate.items[0].name} + ${meal.estimate.items.length - 1} more`;

  async function dismiss() {
    setBusy(true);
    try {
      await dismissShare(item.shareId);
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="p-4 rounded-2xl bg-slate-900 border border-slate-800 border-l-4 border-l-violet-400 space-y-3">
      <div className="flex items-start gap-3">
        <Send className="w-4 h-4 text-violet-300 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-100">
            <span className="font-semibold">{item.sharerEmail}</span>
            <span className="text-slate-400"> shared:</span>
          </div>
          <div className="text-sm text-slate-200 truncate mt-0.5">{name}</div>
          <div className="text-[11px] text-slate-400 tabular-nums mt-0.5">
            {Math.round(meal.estimate.totals.calories)} kcal · P{meal.estimate.totals.protein_g.toFixed(0)} C{meal.estimate.totals.carbs_g.toFixed(0)} F{meal.estimate.totals.fat_g.toFixed(0)}
          </div>
          {item.note && (
            <div className="mt-2 px-3 py-2 rounded-md bg-slate-900/60 border-l-2 border-violet-400 text-xs text-slate-300 italic">
              "{item.note}"
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onOpen}
          disabled={busy}
          className="flex-1 py-2 rounded-lg bg-emerald-500 text-slate-950 text-sm font-semibold disabled:opacity-50"
        >
          Log it →
        </button>
        <button
          onClick={dismiss}
          disabled={busy}
          className="py-2 px-4 rounded-lg border border-slate-700 text-slate-300 text-sm disabled:opacity-50"
        >
          Dismiss
        </button>
      </div>
    </li>
  );
}

function PendingReceivedCard({
  connection,
  onChange
}: {
  connection: Connection;
  onChange: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDecline, setConfirmingDecline] = useState(false);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await acceptConnection(connection.id);
      await onChange();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    setConfirmingDecline(false);
    setBusy(true);
    try {
      await disconnect(connection.id);
      await onChange();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <li className="p-4 rounded-2xl bg-slate-900 border border-slate-800 border-l-4 border-l-emerald-400 space-y-3">
      <div className="text-sm text-slate-100">
        <span className="font-semibold">{connection.partnerEmail}</span>
        <span className="text-slate-400"> wants to connect with you.</span>
      </div>
      {error && (
        <div className="text-xs text-rose-300">{error}</div>
      )}
      <div className="flex gap-2">
        <button
          onClick={accept}
          disabled={busy}
          className="flex-1 py-2 rounded-lg bg-emerald-500 text-slate-950 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <Check className="w-4 h-4" /> Accept
        </button>
        <button
          onClick={() => setConfirmingDecline(true)}
          disabled={busy}
          className="py-2 px-4 rounded-lg border border-slate-700 text-slate-300 text-sm disabled:opacity-50"
        >
          Decline
        </button>
      </div>
    </li>
    {confirmingDecline && (
      <ConfirmModal
        title="Decline this invite?"
        description={`${connection.partnerEmail} won't be notified. They can re-invite you later.`}
        confirmLabel="Decline"
        destructive
        onConfirm={decline}
        onCancel={() => setConfirmingDecline(false)}
      />
    )}
    </>
  );
}

function PendingSentCard({
  connection,
  onChange
}: {
  connection: Connection;
  onChange: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  async function cancel() {
    setConfirming(false);
    setBusy(true);
    try {
      await disconnect(connection.id);
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <li className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center gap-3">
      <Loader2 className="w-4 h-4 text-slate-500 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-100 truncate">{connection.partnerEmail}</div>
        <div className="text-[11px] text-slate-500">
          {connection.partnerId ? "Invite sent — waiting for them to accept" : "Invite emailed — they'll see it after signing up"}
        </div>
      </div>
      <button
        onClick={() => setConfirming(true)}
        disabled={busy}
        className="text-xs text-slate-500 hover:text-rose-400 disabled:opacity-50"
        aria-label="Cancel invite"
      >
        <X className="w-4 h-4" />
      </button>
    </li>
    {confirming && (
      <ConfirmModal
        title="Cancel invite?"
        description={`The invite to ${connection.partnerEmail} will be removed.`}
        confirmLabel="Cancel invite"
        destructive
        onConfirm={cancel}
        onCancel={() => setConfirming(false)}
      />
    )}
    </>
  );
}

function ActivePartnerCard({
  connection,
  onChange,
  onOpenDay
}: {
  connection: Connection;
  onChange: () => Promise<void>;
  onOpenDay: () => void;
}) {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDisc, setConfirmingDisc] = useState(false);

  async function changePrivacy(tier: PrivacyTier) {
    if (tier === connection.iShare) {
      setPrivacyOpen(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setMyPrivacy(connection, tier);
      await onChange();
      setPrivacyOpen(false);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function disc() {
    setConfirmingDisc(false);
    setBusy(true);
    try {
      await disconnect(connection.id);
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <li className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-base font-semibold text-slate-300 flex-shrink-0">
          {connection.partnerEmail.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-100 truncate">{connection.partnerEmail}</div>
          <div className="text-[11px] text-slate-500">
            They share <span className="text-emerald-400">{PRIVACY_LABELS[connection.theyShare]}</span> with you
          </div>
        </div>
        {connection.partnerId && (
          <button
            type="button"
            onClick={onOpenDay}
            className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-medium text-slate-200"
          >
            Open day →
          </button>
        )}
      </div>

      {/* I share with them — tap to change */}
      <div>
        <button
          type="button"
          onClick={() => setPrivacyOpen((v) => !v)}
          className="w-full text-left p-2.5 rounded-lg bg-slate-800/60 border border-slate-700/60 hover:border-slate-600 text-xs"
        >
          <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-0.5">You share with them</div>
          <div className="flex items-center justify-between">
            <span className="text-slate-100 font-medium">{PRIVACY_LABELS[connection.iShare]}</span>
            <span className="text-slate-500">Change ↓</span>
          </div>
        </button>
        {privacyOpen && (
          <div className="mt-1.5 p-2 rounded-lg bg-slate-950 border border-slate-800 space-y-1">
            {PRIVACY_TIERS.map((tier) => (
              <button
                key={tier}
                type="button"
                onClick={() => changePrivacy(tier)}
                disabled={busy}
                className={`w-full text-left px-3 py-2 rounded-md text-xs ${
                  tier === connection.iShare
                    ? "bg-emerald-950 border border-emerald-700 text-emerald-100"
                    : "hover:bg-slate-800/60 text-slate-300 border border-transparent"
                } disabled:opacity-50`}
              >
                <div className="font-medium">{PRIVACY_LABELS[tier]}</div>
                <div className="text-[11px] text-slate-500">{PRIVACY_DESCRIPTIONS[tier]}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-300">{error}</div>
      )}

      <button
        type="button"
        onClick={() => setConfirmingDisc(true)}
        disabled={busy}
        className="text-[11px] text-slate-500 hover:text-rose-400 disabled:opacity-50"
      >
        Disconnect
      </button>
    </li>
    {confirmingDisc && (
      <ConfirmModal
        title={`Disconnect from ${connection.partnerEmail}?`}
        description="They'll lose access to your data immediately. You can re-invite them later."
        confirmLabel="Disconnect"
        destructive
        onConfirm={disc}
        onCancel={() => setConfirmingDisc(false)}
      />
    )}
    </>
  );
}

// ----------------------------------------------------------------------------
// Misc
// ----------------------------------------------------------------------------

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{title}</h2>
        {subtitle && <span className="text-[11px] text-slate-500">{subtitle}</span>}
      </div>
      {children}
    </section>
  );
}
